import {
  ApiOptions,
  BodyType,
  HeadersType,
  IdsType,
  MaybeIdsType,
  Method,
  ParamsType,
  RequestOptions,
} from '@thyme/libs/src/api/types/api'
import { NullableFieldsOptional } from '@thyme/libs/src/typing/partial'
import { ListResponse } from './types/crud'
import { createUrlWithId } from './url'

export type CrudApiOptions = {
  defaultOptions?: ApiOptions
}

export type DefaultPartialUpdateType<T, IdKeys extends keyof T> = Omit<
  Partial<T>,
  IdKeys
>
export type DefaultCreateType<
  T,
  IdKeys extends keyof T,
> = NullableFieldsOptional<Omit<T, IdKeys>>

// A function that makes API requests in an application-specific way.
// Each app should provide its own implementation.
type ApiRequestFn = <R>(
  method: Method,
  endpoint: string,
  options?: RequestOptions
) => Promise<R>

const LIST_ALL_PAGE_LENGTH = 100
const MAX_PAGE_COUNT_SANITY_CHECK = 10

/**
 * Creates a function to create a CRUD API wrapper around an endpoint.
 * The outer function is for application-wide use, the inner function is for per-resource use.
 *
 * Foundation layer for Vue Query - use with raw useQuery/useMutation hooks.
 * @param apiRequestFn - the function that makes API requests in an application-specific way.
 * @param metaOptions - meta options for all CRUD APIs in this application.
 *
 * See CRUD_README.md for usage and examples.
 */
export function setupCrudApiFactory(
  apiRequestFn: ApiRequestFn,
  metaOptions: CrudApiOptions = {}
) {
  // Returns functions for list, retrieve, create, update, delete operations.
  // url: endpoint url
  // apiOptions: default options to include in all requests (headers, params, etc)
  return <
    ResponseT,
    IdKey extends keyof ResponseT = never,
    CreateT extends BodyType = DefaultCreateType<ResponseT, IdKey>,
    PartialUpdateT extends BodyType = DefaultPartialUpdateType<
      ResponseT,
      IdKey
    >,
  >(
    url: string,
    apiOptions: CrudApiOptions = {}
  ) => ({
    async apiAction<R extends ResponseT | ListResponse<ResponseT>>(
      method: Method,
      requestOptions: RequestOptions,
      ids: MaybeIdsType
    ): Promise<R> {
      // Merge precedence: metaOptions < apiOptions < requestOptions
      const mergedOptions: RequestOptions = {
        ...metaOptions.defaultOptions,
        ...apiOptions.defaultOptions,
        ...requestOptions,
        headers: {
          ...metaOptions.defaultOptions?.headers,
          ...apiOptions.defaultOptions?.headers,
          ...requestOptions.headers,
        },
        params: {
          ...metaOptions.defaultOptions?.params,
          ...apiOptions.defaultOptions?.params,
          ...requestOptions.params,
        },
      }

      return await apiRequestFn<R>(
        method,
        createUrlWithId(url, ids),
        mergedOptions
      )
    },
    async list({
      headers = {},
      params = {},
      ids = null,
    }: {
      headers?: HeadersType
      params?: ParamsType
      ids?: MaybeIdsType
    }): Promise<ListResponse<ResponseT>> {
      return await this.apiAction(
        'GET',
        {
          headers,
          params,
        },
        ids
      )
    },
    /**
     * Fetches a single item from a list endpoint.
     * Automatically sets page_length: 1 to optimize the query.
     * Use this when you expect exactly one result (e.g., filtering by unique field).
     * Returns null if no results found.
     * @param root0
     * @param root0.headers
     * @param root0.params
     * @param root0.ids
     */
    async listOne({
      headers = {},
      params = {},
      ids = null,
    }: {
      headers?: HeadersType
      params?: ParamsType
      ids?: MaybeIdsType
    }): Promise<ResponseT | null> {
      const response = await this.list({
        headers,
        params: { ...params, page_length: 1 },
        ids,
      })
      return response.data[0] ?? null
    },
    async listAll({
      headers = {},
      params = {},
      ids = null,
      maxListAllPagesAllowed = MAX_PAGE_COUNT_SANITY_CHECK,
    }: {
      headers?: HeadersType
      params?: ParamsType
      ids?: MaybeIdsType
      maxListAllPagesAllowed?: number
    }): Promise<ListResponse<ResponseT>> {
      // Calls list repeatedly starting at page 1 until all pages are loaded, using `LIST_ALL_PAGE_LENGTH` (100)
      const pageLength = LIST_ALL_PAGE_LENGTH
      let currentPage = 1

      let itemsToGrab = 0 // Will be updated after first request
      let grabbedItems = 0
      let allData: ResponseT[] = []

      do {
        if (currentPage > maxListAllPagesAllowed) {
          console.error(
            'Exceeded maximum page count sanity check in listAll',
            url,
            params
          )
          break
        }
        const response = await this.list({
          headers,
          params: {
            ...params,
            page_length: pageLength,
            page_number: currentPage,
          },
          ids,
        })

        allData = [...allData, ...response.data]
        grabbedItems += response.data.length
        itemsToGrab = response.queryMetadata.total ?? itemsToGrab
        currentPage++
      } while (itemsToGrab > grabbedItems)

      return {
        data: allData,
        queryMetadata: { total: itemsToGrab },
      }
    },
    async retrieve({
      headers = {},
      params = {},
      ids = null,
    }: {
      headers?: HeadersType
      params?: ParamsType
      ids?: MaybeIdsType
    }): Promise<ResponseT> {
      return await this.apiAction(
        'GET',
        {
          headers,
          params,
        },
        ids
      )
    },
    async partialUpdate({
      ids,
      headers = {},
      params = {},
      body,
    }: {
      headers?: HeadersType
      params?: ParamsType
      body?: PartialUpdateT
      ids: IdsType
    }): Promise<ResponseT> {
      return await this.apiAction(
        'PATCH',
        {
          headers,
          params,
          body,
        },
        ids
      )
    },
    async create({
      ids = null,
      headers = {},
      params = {},
      body,
    }: {
      headers?: HeadersType
      params?: ParamsType
      body?: CreateT
      ids?: MaybeIdsType
    }): Promise<ResponseT> {
      return await this.apiAction(
        'POST',
        {
          headers,
          params,
          body,
        },
        ids
      )
    },
    async upsert({
      ids = null,
      headers = {},
      params = {},
      body,
    }: {
      headers?: HeadersType
      params?: ParamsType
      body?: CreateT
      ids?: MaybeIdsType
    }): Promise<ResponseT> {
      return await this.apiAction(
        'PUT',
        {
          headers,
          params,
          body,
        },
        ids
      )
    },
    async delete({
      ids = null,
      headers = {},
      params = {},
    }: {
      headers?: HeadersType
      params?: ParamsType
      ids?: MaybeIdsType
    }): Promise<void> {
      await this.apiAction(
        'DELETE',
        {
          headers,
          params,
        },
        ids
      )
    },
  })
}
